import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { MasterDataListItem } from '../../components/MasterDataListItem';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { getOrganizationModuleCapabilities } from '../../config/organizationCapabilities';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useTeams } from '../../hooks/useTeams';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';

type Props = NativeStackScreenProps<RootStackParamList, 'TeamList'>;

export function TeamListScreen({ navigation }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      chip: {
        borderWidth: 1,
        borderColor: palette.border,
        borderRadius: 999,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        backgroundColor: palette.surface,
      },
      chipText: { ...typography.caption, color: palette.textPrimary },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getOrganizationModuleCapabilities(auth.permissions);
  const list = useTeams();

  const footer = useMemo(() => {
    if (!list.isLoadingMore) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [list.isLoadingMore, palette.primary]);

  const hasFilters = Boolean(list.search.trim()) || list.filters.isActive != null;

  return (
    <PermissionGuard
      permission={PERMISSIONS.TEAM_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen}>
        <ScreenHeader
          title="Teams"
          onBack={navigation.goBack}
        />
        <View style={styles.body}>
        <AppInput
          label="Search"
          value={list.search}
          onChangeText={list.setSearch}
          placeholder="Code or name"
          autoCapitalize="none"
        />
        <View style={styles.filterRow}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show active teams"
            onPress={() => list.applyFilters({ isActive: true })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Active</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Show inactive teams"
            onPress={() => list.applyFilters({ isActive: false })}
            style={themed.chip}
          >
            <Text style={themed.chipText}>Inactive</Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear status filter"
            onPress={() => list.applyFilters({})}
            style={themed.chip}
          >
            <Text style={themed.chipText}>All</Text>
          </Pressable>
        </View>

        {capabilities.canCreateTeam ? (
          <AppButton
            title="Create team"
            onPress={() => navigation.navigate(routes.teamCreate)}
          />
        ) : null}

        {list.isLoading && list.items.length === 0 ? (
          <View style={styles.center}>
            <ActivityIndicator color={palette.primary} />
          </View>
        ) : list.error && list.items.length === 0 ? (
          <ErrorState message={list.error} onRetry={list.retry} />
        ) : (
          <FlatList
            data={list.items}
            keyExtractor={item => String(item.teamId)}
            refreshControl={
              <RefreshControl
                refreshing={list.isRefreshing}
                onRefresh={list.refresh}
              />
            }
            onEndReached={list.loadMore}
            onEndReachedThreshold={0.4}
            ListFooterComponent={footer}
            ListEmptyComponent={
              <EmptyState
                title={
                  hasFilters
                    ? 'No records match the current search.'
                    : 'No teams were found.'
                }
                message={
                  hasFilters
                    ? 'Try clearing search or filters.'
                    : 'Create a team when you have permission.'
                }
                actionTitle={hasFilters ? 'Reset filters' : 'Retry'}
                onAction={hasFilters ? list.resetFilters : list.retry}
              />
            }
            contentContainerStyle={styles.listContent}
            renderItem={({ item }) => (
              <MasterDataListItem
                title={item.teamName}
                code={item.teamCode}
                subtitle={
                  [item.departmentName, item.locationName, item.teamLeadName]
                    .filter(Boolean)
                    .join(' · ') || null
                }
                status={item.statusLabel}
                onPress={() =>
                  navigation.navigate(routes.teamDetails, {
                    teamId: item.teamId,
                  })
                }
              />
            )}
          />
        )}

        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.sm },
  filterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
